import React, { useEffect, useState } from 'react'
import { CircularProgress } from '@mui/material'
import {
  CopySuggestion,
  CopyGenerationResponse,
  SUPPORTED_CALL_TO_ACTIONS,
} from '@marketing-milk/interfaces'
import { useAIStudio } from '../useAIStudio'
import { CopyPreviewModal } from './CopyPreviewModal'
import { toast } from 'react-toastify'

type Props = {
  businessID: string
  jobID: string | null
  onSelectSuggestion: (suggestion: CopySuggestion) => void
}

export function CopyGenerationPanel({ businessID, jobID, onSelectSuggestion }: Props) {
  const { generateCopy, generateCopyPreview } = useAIStudio(businessID)
  const [userHint, setUserHint] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<CopyGenerationResponse | null>(null)
  const [previewing, setPreviewing] = useState<CopySuggestion | null>(null)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewLoadingIndex, setPreviewLoadingIndex] = useState<number | null>(null)

  const handleGenerate = async () => {
    setIsLoading(true)
    setResult(null)
    try {
      const response = await generateCopy({
        jobID: jobID ?? undefined,
        userHint: userHint.trim() || undefined,
        count: 3,
      })
      setResult(response)
    } catch {
      toast.error('Failed to generate copy. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleUse = async (suggestion: CopySuggestion, index: number) => {
    if (!jobID) {
      onSelectSuggestion(suggestion)
      return
    }
    setPreviewLoadingIndex(index)
    try {
      const { previewHtml: html } = await generateCopyPreview(jobID, suggestion)
      setPreviewing(suggestion)
      setPreviewHtml(html)
    } catch {
      toast.error('Failed to generate preview. You can still import directly.')
      onSelectSuggestion(suggestion)
    } finally {
      setPreviewLoadingIndex(null)
    }
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">Brand Voice Copy</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Generate copy based on your brand's existing ads
            </p>
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {isLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <i className="fa fa-wand-sparkles" />
            )}
            Generate Copy
          </button>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Optional guidance</label>
          <input
            type="text"
            value={userHint}
            onChange={e => setUserHint(e.target.value)}
            placeholder='e.g. "make it feel more premium"'
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {isLoading && (
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className="rounded-xl border border-gray-200 p-4 flex flex-col gap-2 animate-pulse"
              >
                <div className="h-3 bg-gray-200 rounded w-2/3" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
                <div className="h-3 bg-gray-200 rounded w-3/4" />
              </div>
            ))}
          </div>
        )}

        {result && !isLoading && (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-gray-500">
              Based on {result.brandVoiceSampleCount} of your existing ads
            </p>
            {result.suggestions.map((suggestion, i) => (
              <SuggestionCard
                key={i}
                index={i + 1}
                suggestion={suggestion}
                isLoadingPreview={previewLoadingIndex === i}
                onUse={edited => handleUse(edited, i)}
                onImport={edited => onSelectSuggestion(edited)}
              />
            ))}
          </div>
        )}
      </div>

      {previewing && previewHtml && (
        <CopyPreviewModal
          previewHtml={previewHtml}
          suggestion={previewing}
          onImport={() => {
            onSelectSuggestion(previewing)
            setPreviewing(null)
            setPreviewHtml(null)
          }}
          onClose={() => {
            setPreviewing(null)
            setPreviewHtml(null)
          }}
        />
      )}
    </>
  )
}

type SuggestionCardProps = {
  index: number
  suggestion: CopySuggestion
  isLoadingPreview: boolean
  onUse: (edited: CopySuggestion) => void
  onImport: (edited: CopySuggestion) => void
}

function SuggestionCard({
  index,
  suggestion,
  isLoadingPreview,
  onUse,
  onImport,
}: SuggestionCardProps) {
  const [edited, setEdited] = useState<CopySuggestion>({ ...suggestion })

  useEffect(() => {
    setEdited({ ...suggestion })
  }, [suggestion])

  const update = (field: keyof CopySuggestion, value: string) =>
    setEdited(prev => ({ ...prev, [field]: value }))

  return (
    <div className="rounded-xl border border-gray-200 p-4 flex flex-col gap-2 hover:border-purple-300 transition-colors">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
          Option {index}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onUse(edited)}
            disabled={isLoadingPreview}
            className="flex items-center gap-1.5 px-3 py-1 rounded-lg border border-purple-600 text-purple-600 text-xs font-semibold hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingPreview ? (
              <CircularProgress size={10} color="inherit" />
            ) : (
              <i className="fa fa-eye" />
            )}
            Preview
          </button>
          <button
            type="button"
            onClick={() => onImport(edited)}
            disabled={isLoadingPreview}
            className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="fa fa-file-import" />
            Import
          </button>
        </div>
      </div>

      <CopyField
        label="Headline"
        value={edited.headline}
        limit={40}
        onChange={v => update('headline', v)}
      />
      <CopyField
        label="Description"
        value={edited.description}
        limit={30}
        onChange={v => update('description', v)}
      />
      {(edited.graphicText || suggestion.graphicText) && (
        <CopyField
          label="Graphic Text"
          value={edited.graphicText}
          limit={20}
          onChange={v => update('graphicText', v)}
        />
      )}
      <CopyField
        label="Primary Text"
        value={edited.primaryText}
        limit={125}
        multiline
        onChange={v => update('primaryText', v)}
      />
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 w-24 flex-shrink-0">CTA</span>
        <select
          value={edited.callToAction}
          onChange={e => update('callToAction', e.target.value)}
          className="flex-1 text-xs text-gray-800 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-400 focus:border-purple-400 bg-white"
        >
          {SUPPORTED_CALL_TO_ACTIONS.map(cta => (
            <option key={cta} value={cta}>
              {cta.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

function CopyField({
  label,
  value,
  limit,
  multiline,
  onChange,
}: {
  label: string
  value: string
  limit: number
  multiline?: boolean
  onChange: (value: string) => void
}) {
  const overLimit = value.length > limit
  return (
    <div className="flex items-start gap-2">
      <span className="text-xs text-gray-500 w-24 flex-shrink-0 pt-2">{label}</span>
      <div className="flex-1 flex flex-col gap-0.5">
        {multiline ? (
          <textarea
            value={value}
            onChange={e => onChange(e.target.value)}
            rows={3}
            className="w-full text-xs text-gray-800 border border-gray-200 rounded-lg px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-purple-400 focus:border-purple-400"
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            className="w-full text-xs text-gray-800 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-400 focus:border-purple-400"
          />
        )}
        <span
          className={`text-xs text-right ${
            overLimit ? 'text-red-500 font-medium' : 'text-gray-400'
          }`}
        >
          {value.length}/{limit}
        </span>
      </div>
    </div>
  )
}
